/**
 * employeeOnboardingSteps — LBR2-C3 (2026-07-26)
 *
 * PURE step model + validators for the employee onboarding wizard. No React,
 * no network → unit-testable. The wizard component consumes these to drive
 * step navigation, per-step validation, and the final submit gate.
 */

export const KYC_DOC_TYPES = ['PAN', 'AADHAAR', 'PASSPORT', 'BANK', 'PHOTO', 'OTHER'] as const;
export type KycDocType = (typeof KYC_DOC_TYPES)[number];

/**
 * 🔒 2026-09-05 — `Employee.maritalStatus` and `Employee.bloodGroup` are POSTGRES
 * ENUMS (`prisma/schema.prisma`: `enum MaritalStatus`, `enum BloodGroup`). These
 * two fields were rendered as free-text inputs, so typing "Married" or "B+"
 * threw a Prisma validation error and took the WHOLE save down with it — the
 * edit was lost, not just those two fields.
 *
 * The values below are the enum members verbatim; the labels are display only.
 * Blank stays valid — both columns are optional. Shared by create and edit,
 * because the wizard is one component.
 */
export const MARITAL_STATUS_OPTIONS = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'MARRIED', label: 'Married' },
  { value: 'DIVORCED', label: 'Divorced' },
  { value: 'WIDOWED', label: 'Widowed' },
] as const;

export const BLOOD_GROUP_OPTIONS = [
  { value: 'A_POS', label: 'A+' },
  { value: 'A_NEG', label: 'A-' },
  { value: 'B_POS', label: 'B+' },
  { value: 'B_NEG', label: 'B-' },
  { value: 'AB_POS', label: 'AB+' },
  { value: 'AB_NEG', label: 'AB-' },
  { value: 'O_POS', label: 'O+' },
  { value: 'O_NEG', label: 'O-' },
] as const;

export const ONBOARDING_STEPS = [
  { id: 'identity', label: 'Identity & Login' },
  { id: 'personal', label: 'Personal & KYC' },
  { id: 'bank', label: 'Bank & Address' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'documents', label: 'Documents & Review' },
] as const;
export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]['id'];

export interface OnboardingForm {
  // identity / login
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roleId: string;
  employeeCode: string;
  phone: string;
  designation: string;
  departmentId: string;
  dateOfJoining: string;
  salary: string;
  // personal / KYC
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  bloodGroup: string;
  pan: string;
  aadhaarNumber: string;
  passportNumber: string;
  personalEmail: string;
  // bank + address
  bankName: string;
  bankAccountNo: string;
  currentAddress: string;
  permanentAddress: string;
  personToBeContacted: string;
  emergencyPhoneNumber: string;
  emergencyRelation: string;
  // vehicle
  vehicleRegNo: string;
  vehicleType: string;
  fuelType: string;
}

export const emptyOnboardingForm: OnboardingForm = {
  firstName: '', lastName: '', email: '', password: '', roleId: '', employeeCode: '',
  phone: '', designation: '', departmentId: '', dateOfJoining: '', salary: '',
  dateOfBirth: '', gender: '', maritalStatus: '', bloodGroup: '', pan: '',
  aadhaarNumber: '', passportNumber: '', personalEmail: '',
  bankName: '', bankAccountNo: '', currentAddress: '', permanentAddress: '',
  personToBeContacted: '', emergencyPhoneNumber: '', emergencyRelation: '',
  vehicleRegNo: '', vehicleType: '', fuelType: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/**
 * 2026-09-05 — the wizard now serves BOTH paths. Create is unchanged. Edit runs
 * the same five steps against an existing record, so two identity rules soften:
 *
 *  - password: the server holds a hash and there is nothing to prefill, so BLANK
 *    means "leave it alone". Anything actually typed is still held to the same
 *    bar as create.
 *  - roleId: `GET /employees/:id` returns the Employee, which carries no role
 *    (the role lives on the linked User), and `PUT /employees/:id` does not take
 *    one. Requiring a role on edit would block every save on a value we cannot
 *    read and cannot write.
 *
 * Everything else is shared — ONE validator for both paths, which is the point.
 */
export type WizardMode = 'create' | 'edit';

/**
 * 🔒 SIX, and it must stay the same number the server uses
 * (`assertOnboardingComplete` in backend `employee-onboarding.ts`). The wizard
 * and the submit gate both read this constant so a blank-vs-typed password
 * cannot be judged by two different bars — a typed password that clears the
 * form but is then dropped by the caller is a silent no-op edit.
 */
export const MIN_PASSWORD_LENGTH = 6;

/** Per-step validation → list of human error strings (empty = valid). */
export function validateStep(
  stepId: OnboardingStepId,
  form: OnboardingForm,
  mode: WizardMode = 'create',
): string[] {
  const errs: string[] = [];
  if (stepId === 'identity') {
    if (!form.firstName.trim()) errs.push('First name is required');
    if (!form.lastName.trim()) errs.push('Last name is required');
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) errs.push('A valid email is required');
    const pwd = form.password.trim();
    if (mode === 'create') {
      if (pwd.length < MIN_PASSWORD_LENGTH) errs.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
    } else if (pwd.length > 0 && pwd.length < MIN_PASSWORD_LENGTH) {
      errs.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
    }
    if (mode === 'create' && !form.roleId.trim()) errs.push('A role is required');
  }
  if (stepId === 'personal') {
    if (form.pan.trim() && !PAN_RE.test(form.pan.trim())) {
      errs.push('PAN must be 5 letters, 4 digits, 1 letter (e.g. ABCPK1234F)');
    }
  }
  // bank / vehicle / documents steps are optional enrichment — no hard rules.
  return errs;
}

export function isStepComplete(
  stepId: OnboardingStepId,
  form: OnboardingForm,
  mode: WizardMode = 'create',
): boolean {
  return validateStep(stepId, form, mode).length === 0;
}

/** The wizard can submit once the identity step is valid (rest is enrichment). */
export function canSubmit(form: OnboardingForm, mode: WizardMode = 'create'): boolean {
  return isStepComplete('identity', form, mode);
}

export function kycDocTypeLabel(t: KycDocType): string {
  switch (t) {
    case 'PAN': return 'PAN card';
    case 'AADHAAR': return 'Aadhaar card';
    case 'PASSPORT': return 'Passport';
    case 'BANK': return 'Bank proof';
    case 'PHOTO': return 'Photo';
    case 'OTHER': return 'Other document';
  }
}
